// shared/telemetry-events.js
//
// Canonical Stream B UI-event vocabulary: the wire type string, a human
// display label, and the feature group the researcher dashboard buckets it
// into, for every recordUiEvent/recordEvent call site in the participant
// tool. Lives in shared/ (not frontend/harness/) for the same reason as
// shared/experiment-arms.js: two independent consumers need the same
// canonical data without either importing the other's tree. The harness
// telemetry sink (`frontend/harness/telemetry.js`) validates emitted types
// against it; the researcher dashboard SPA (`frontend/dashboard/behavior-labels.js`)
// reads it for display and must not import participant/harness code
// (see `docs/dashboard.md`).
//
// TelemetryEvent is the interface call sites use instead of spelling the
// wire string by hand — a typo becomes a broken import or an `undefined`
// constant instead of a silently-invented event with its own
// single-occurrence row in the dashboard's Behavior panel. Wire strings
// here must never change: the dashboard's SQLite already holds rows keyed
// by these exact values.

const REGISTRY = [
  // [wire type, display label, dashboard feature group]
  ['session_started', 'Session started', 'Session'],
  ['feature_revealed', 'Feature revealed', 'Session'],
  ['message_sent', 'Message sent', 'Session'],
  ['send_attempt', 'Send attempted', 'Session'],
  ['finish_requested', 'Finish requested', 'Session'],
  ['finish_cancelled', 'Finish cancelled', 'Session'],
  ['study_complete', 'Study completed', 'Session'],
  ['handoff_unavailable', 'Handoff unavailable', 'Session'],

  ['report_gate_shown', 'Report gate shown', 'Report Gate'],
  ['report_gate_resumed', 'Report gate resumed', 'Report Gate'],
  ['report_gate_cleared', 'Report gate cleared', 'Report Gate'],
  ['report_gate_unavailable', 'Report gate unavailable', 'Report Gate'],

  ['f1_mirror_open', 'Report opened', 'F1 Report'],
  ['f1_mirror_close', 'Report closed', 'F1 Report'],
  ['f1_category_selected', 'Report category selected', 'F1 Report'],
  ['f1_evidence_jump', 'Report evidence viewed', 'F1 Report'],

  ['redact_invoked', 'Redact used', 'F2 What-if'],
  ['redact_attempt', 'SAFER attempted', 'F2 What-if'],
  ['redact_outcome', 'SAFER outcome', 'F2 What-if'],
  ['draft_score_requested', 'Draft score requested', 'F2 What-if'],
  ['draft_score_settled', 'Draft score settled', 'F2 What-if'],
  ['f2_span_toggle', 'What-if span opened', 'F2 What-if'],
  ['f2_span_close', 'What-if span closed', 'F2 What-if'],

  ['hardstop_shown', 'Hard-stop shown', 'F2 Hard-stop'],
  ['hardstop_send_blocked_by_limit', 'Hard-stop send blocked by limit', 'F2 Hard-stop'],
  ['hardstop_ack', 'Hard-stop acknowledged', 'F2 Hard-stop'],
  ['hardstop_cancel', 'Hard-stop cancelled', 'F2 Hard-stop'],
  ['hardstop_redact_invoked', 'Hard-stop redact used', 'F2 Hard-stop'],
  ['hardstop_ack_toggled', 'Hard-stop ack toggled', 'F2 Hard-stop'],
  ['hardstop_redaction_span_toggle', 'Hard-stop span toggled', 'F2 Hard-stop'],
  ['hardstop_restore_all', 'Hard-stop restore all', 'F2 Hard-stop'],

  ['f3_meter_expand', 'Risk meter expanded', 'F3 Meter'],
  ['f3_meter_close', 'Risk meter closed', 'F3 Meter'],
  ['f3_drilldown_open', 'Meter drill-down opened', 'F3 Meter'],
  ['f3_drilldown_close', 'Meter drill-down closed', 'F3 Meter'],
  ['f3_axis_selected', 'Meter axis selected', 'F3 Meter'],
  ['f3_evidence_jump', 'Meter evidence viewed', 'F3 Meter'],

  ['f4_hotspot_toggle', 'Hotspot tint toggled', 'F4 Hotspot'],
  ['f4_hotspot_open', 'Hotspot opened', 'F4 Hotspot'],
];

/** @type {Readonly<Record<string, string>>} */
export const TelemetryEvent = Object.freeze(
  Object.fromEntries(REGISTRY.map(([type]) => [type.toUpperCase(), type]))
);

const DETAILS = new Map(
  REGISTRY.map(([type, label, group], order) => [type, { label, group, order }])
);

const FALLBACK_ORDER = REGISTRY.length;

export function isKnownEventType(type) {
  return DETAILS.has(type);
}

/**
 * @param {string} type
 * @returns {{label: string, group: string, order: number}}
 */
export function describeEvent(type) {
  return DETAILS.get(type) ?? { label: type, group: 'Other', order: FALLBACK_ORDER };
}

export function eventOrder(type) {
  return describeEvent(type).order;
}
